import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import type { RootState } from "../../store";
import { fetchAgents } from "../../store/agentSlice";
import {
  submitDetailedFeedback,
  resetFeedbackState,
} from "../../store/feedbackSlice";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import Sidebar from "../../components/Sidebar";
import {
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import "../Dashboard.scss";
import styles from "./feedback.module.scss";

const ALLOWED_FILE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/pdf": [".pdf"],
};

const ALLOWED_EXTENSIONS = ".jpg,.jpeg,.png,.pdf";
const MAX_FILE_SIZE_MB = 10;

// Allowed agent IDs for feedback dropdown
const ALLOWED_AGENT_IDS = [27, 20, 23, 1, 24, 106, 46, 7, 18, 11, 21];

export default function FeedbackPage(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const agents = useSelector((state: RootState) => state.agents.data);
  const agentsLoading = useSelector((state: RootState) => state.agents.loading);
  const feedbackLoading = useSelector(
    (state: RootState) => state.feedback.loading,
  );
  const feedbackSuccess = useSelector(
    (state: RootState) => state.feedback.success,
  );
  const feedbackError = useSelector((state: RootState) => state.feedback.error);

  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [feedbackText, setFeedbackText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [dispatch, agents.length]);

  useEffect(() => {
    if (feedbackSuccess) {
      setSelectedAgentId("");
      setFeedbackText("");
      setFile(null);
      setFileError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      const timer = setTimeout(() => {
        dispatch(resetFeedbackState());
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [feedbackSuccess, dispatch]);

  const handleAgentChange = (event: SelectChangeEvent<string>) => {
    setSelectedAgentId(event.target.value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    setFileError("");

    if (!chosen) {
      setFile(null);
      return;
    }

    const isValidType = Object.keys(ALLOWED_FILE_TYPES).includes(chosen.type);
    if (!isValidType) {
      setFileError("Only images (JPG, PNG) and PDF files are allowed.");
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const fileSizeMB = chosen.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      setFileError(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFile(chosen);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAgentId || !feedbackText.trim()) {
      return;
    }

    const selectedAgent = agents.find(
      (agent) => String(agent.id) === selectedAgentId,
    );

    if (!selectedAgent) {
      return;
    }

    await dispatch(
      submitDetailedFeedback({
        agent_name: selectedAgent.agent_name,
        feedback: feedbackText,
        image: file || undefined,
      }),
    );
  };

  const isSubmitDisabled =
    !selectedAgentId || !feedbackText.trim() || feedbackLoading;

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="feedback"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <h1 className="page-title">Feedback</h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            <form onSubmit={handleSubmit} className={styles.formCard}>
              {feedbackSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Thanks for submitting your feedback. We will consider it for
                  future upgrades
                </Alert>
              )}
              {feedbackError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {feedbackError}
                </Alert>
              )}
              {fileError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {fileError}
                </Alert>
              )}

              <div className={styles.formRow}>
                <FormControl fullWidth size="medium" required>
                  <InputLabel id="feedback-agent-label">Agent Name</InputLabel>
                  <Select
                    labelId="feedback-agent-label"
                    id="feedback-agent"
                    value={selectedAgentId}
                    label="Agent Name"
                    onChange={handleAgentChange}
                    disabled={agentsLoading || feedbackLoading}
                  >
                    <MenuItem value="">
                      <em>Select an agent</em>
                    </MenuItem>
                    {agents
                      .filter((agent) => ALLOWED_AGENT_IDS.includes(agent.id))
                      .sort((a, b) => a.agent_name.localeCompare(b.agent_name))
                      .map((agent) => (
                        <MenuItem key={agent.id} value={String(agent.id)}>
                          {agent.agent_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>

              <div className={styles.formRow}>
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={4}
                  maxRows={12}
                  label="Feedback"
                  placeholder="Write your feedback here..."
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  variant="outlined"
                  size="medium"
                  disabled={feedbackLoading}
                  inputProps={{
                    maxLength: 500,
                  }}
                  helperText={`${feedbackText.length}/500 characters`}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: 'right',
                      marginRight: 0,
                    }
                  }}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.fileInputWrapper}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="feedback-file"
                    className={styles.fileInput}
                    onChange={handleFileChange}
                    accept={ALLOWED_EXTENSIONS}
                    disabled={feedbackLoading}
                  />
                  <label
                    htmlFor="feedback-file"
                    className={styles.fileInputLabel}
                  >
                    <UploadFileIcon fontSize="small" />
                    {file ? file.name : "Choose file to upload"}
                  </label>
                  {file && (
                    <div className={styles.fileInfo}>
                      <span className={styles.fileName}>
                        Selected: {file.name} ({(file.size / 1024).toFixed(1)}{" "}
                        KB)
                      </span>
                      <IconButton
                        size="small"
                        onClick={handleRemoveFile}
                        disabled={feedbackLoading}
                        aria-label="Remove file"
                        sx={{ ml: 1 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                  )}
                </div>
              </div>

              <div className={`${styles.formRow} ${styles.submitRow}`}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isSubmitDisabled}
                  startIcon={
                    feedbackLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {feedbackLoading ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
